import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { getBatchDefinitionsPostData } from 'ducks/batch-definitions/utils';
import { getProject } from 'ducks/project/operators';
import { getProjects } from 'ducks/projects/operators';
import { updateSettings } from 'ducks/settings/slices';
import { Slices } from 'ducks/slices';
import {
  BatchDefinitionDependenciesAPI,
  BatchDefinitionParametersAPI,
  BatchDefinitionsItemAPI,
  ProjectsBatchDefinitionsListAPI,
} from 'ducks/types/api-types';
import { BatchDefinitionsRunPayload } from 'ducks/types/payload-types';
import { Card } from 'ducks/types/prop-types';
import { Query } from 'ducks/types/query-types';
import { Parameters } from 'ducks/types/store-types';
import { store } from 'store';
import { Andromeda } from 'utils/axios';
import { handleError } from 'utils/errors';
import { replaceSearchQuery } from 'utils/queries';

export const getBatchDefinitions = createAsyncThunk<
  ProjectsBatchDefinitionsListAPI[],
  { project_id: number; data?: Query }
>(`${Slices.BATCH_DEFINITIONS}/get`, async ({ project_id, data }, { rejectWithValue }) => {
  try {
    const post_data = getBatchDefinitionsPostData(data);
    const res: AxiosResponse['data'] = await Andromeda.get(
      `/projects/${project_id}/batch_definitions/list${replaceSearchQuery(post_data)}`,
    );
    return res.data;
  } catch (error) {
    handleError(error);
    return rejectWithValue(error as AxiosError);
  }
});

export const getBatchDefinitionsPage = async (project_id: number, data?: Query) => {
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Promise.all([
    store.dispatch(getProjects(data)),
    store.dispatch(getProject(project_id)),
    store.dispatch(getBatchDefinitions({ project_id, data })),
  ]);
  store.dispatch(updateSettings({ loading: false }));
  return res;
};

export const archiveBatchDefinition = async (entity_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  await Andromeda.post(`/batch_definitions/${entity_id}/archive`, {});
};

export const unarchiveBatchDefinition = async (entity_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  await Andromeda.post(`/batch_definitions/${entity_id}/unarchive`, {});
};

export const runBatchDefinition = async (entity_id: number, data: BatchDefinitionsRunPayload) => {
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Andromeda.post(`/batch_definitions/${entity_id}/run`, data);
  return res.data;
};

export const getBatchDefinitionParams = async (entity_id: number): Promise<BatchDefinitionsItemAPI> => {
  const res: AxiosResponse['data'] = await Andromeda.get(`/batch_definitions/${entity_id}`);
  return res.data;
};

export const getBatchDefinitionItemParams = async (entity_id: number): Promise<BatchDefinitionParametersAPI[]> => {
  const res: AxiosResponse['data'] = await Andromeda.get(`/batch_definition_items/${entity_id}/parameters/list`);
  return res.data;
};

export const postBatchDefinitionItemCreateDep = async (entity_id: number, entity_id_finish: number) => {
  const res: AxiosResponse['data'] = await Andromeda.post(
    `/batch_definition_items/${entity_id}/dependencies/${entity_id_finish}/create`,
  );
  return res.data;
};

export const getBatchDefinitionItemCheckDep = async (entity_id: number): Promise<BatchDefinitionDependenciesAPI[]> => {
  const res: AxiosResponse['data'] = await Andromeda.get(`/batch_definition_items/${entity_id}/dependencies/list`);
  return res.data;
};

export const createJobParametersConnect = async (edit_job_extra: number | null, form_state: Parameters) => {
  store.dispatch(updateSettings({ loading: true }));
  const {
    parameter_name,
    parameter_direction_id,
    parameter_method_id,
    is_required,
    parameter_value,
    description,
    command_line_prefix,
    command_line_assignment_char,
    command_line_escaped,
    command_line_ignore_name,
    reference_type_id,
    reference_id,
    reference_parameter_name,
  } = form_state;

  const payload = {
    parameter_name,
    parameter_direction_id,
    parameter_method_id,
    is_required,
    is_encrypted: false,
    parameter_value,
    description,
    command_line_prefix,
    command_line_assignment_char,
    command_line_escaped,
    command_line_ignore_name,
    reference_type_id,
    reference_id,
    reference_parameter_name,
  };

  if (edit_job_extra) {
    await Andromeda.post(
      `/batch_definition_items/${edit_job_extra}/parameters/by_name/${payload.parameter_name}/create`,
      payload,
    );
  }
};

export const deleteBatchItem = async (card: Card) => {
  store.dispatch(updateSettings({ loading: true }));

  const res: AxiosResponse['data'] = await Andromeda.post(
    `/batch_definition_items/${card.batch_definition_item_id}/delete`,
  );
  if (card.parent_item_ids[0]) {
    await Andromeda.post(
      `/batch_definition_items/${card.batch_definition_item_id}/dependencies/${card.parent_item_ids[0]}/delete`,
    );
  }
  if (card.children_item_ids[0]) {
    await Andromeda.post(
      `/batch_definition_items/${card.batch_definition_item_id}/dependencies/${card.children_item_ids[0]}/delete`,
    );
  }

  store.dispatch(updateSettings({ loading: false }));

  return res.data;
};
